import { useEffect, useState } from 'react';
import { Venta } from '@/lib/supabase';
import { VentaDetalleItem, cargarDetalleVenta } from '@/lib/ventas';
import { numeroVenta } from '@/lib/pdf';

interface Props {
  venta: Venta;
  empleado: string;
  onClose: () => void;
  onDescargar: () => void;
  exportando: boolean;
}

const formatearBs = (valor: number): string =>
  `Bs. ${(Number.isFinite(valor) ? valor : 0).toLocaleString('es-BO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function DetalleVentaModal({ venta, empleado, onClose, onDescargar, exportando }: Props) {
  const [items, setItems] = useState<VentaDetalleItem[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let activo = true;
    const cargar = async () => {
      setCargando(true);
      setError('');
      try {
        const data = await cargarDetalleVenta(venta.id);
        if (activo) setItems(data);
      } catch {
        if (activo) setError('No se pudo cargar el detalle de la venta.');
      } finally {
        if (activo) setCargando(false);
      }
    };
    cargar();
    return () => {
      activo = false;
    };
  }, [venta.id]);

  const fecha = new Date(venta.fecha);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>

      <div className="relative w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-[#111827]">Detalle de venta</h3>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-[#2563EB]/10 text-[#2563EB]">
                {numeroVenta(venta.id)}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {fecha.toLocaleDateString('es-BO', { day: '2-digit', month: 'long', year: 'numeric' })} ·{' '}
              {fecha.toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            <div className="bg-[#F4F6F8] rounded-lg p-3">
              <p className="text-[11px] text-gray-500 mb-0.5">Cliente</p>
              <p className="text-sm font-medium text-[#111827] truncate">{venta.nombre_factura || 'Consumidor Final'}</p>
            </div>
            <div className="bg-[#F4F6F8] rounded-lg p-3">
              <p className="text-[11px] text-gray-500 mb-0.5">NIT / CI</p>
              <p className="text-sm font-medium text-[#111827] font-mono truncate">{venta.nit || '—'}</p>
            </div>
            <div className="bg-[#F4F6F8] rounded-lg p-3">
              <p className="text-[11px] text-gray-500 mb-0.5">Empleado</p>
              <p className="text-sm font-medium text-[#111827] truncate">{empleado || '—'}</p>
            </div>
            <div className="bg-[#F4F6F8] rounded-lg p-3">
              <p className="text-[11px] text-gray-500 mb-0.5">Método de pago</p>
              <p className="text-sm font-medium text-[#111827] capitalize">{venta.metodo_pago || '—'}</p>
            </div>
            <div className="bg-[#F4F6F8] rounded-lg p-3">
              <p className="text-[11px] text-gray-500 mb-0.5">Monto recibido</p>
              <p className="text-sm font-medium text-[#111827]">{formatearBs(Number(venta.monto_recibido))}</p>
            </div>
            <div className="bg-[#F4F6F8] rounded-lg p-3">
              <p className="text-[11px] text-gray-500 mb-0.5">Cambio</p>
              <p className="text-sm font-medium text-[#111827]">{formatearBs(Number(venta.cambio))}</p>
            </div>
          </div>

          <h4 className="text-sm font-semibold text-[#111827] mb-2">Productos vendidos</h4>

          {cargando ? (
            <div className="flex items-center justify-center py-10">
              <div className="spinner w-8 h-8 border-3 border-[#2563EB] border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="py-6 text-center text-sm text-red-600 bg-red-50 rounded-lg">{error}</div>
          ) : items.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-500 bg-[#F4F6F8] rounded-lg">
              Esta venta no tiene productos registrados.
            </div>
          ) : (
            <div className="border border-gray-100 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F4F6F8] text-left">
                    <th className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase">Producto</th>
                    <th className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase text-center">Cant.</th>
                    <th className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase text-right">P. Unit.</th>
                    <th className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-t border-gray-50">
                      <td className="px-3 py-2.5">
                        <p className="text-sm text-[#111827]">{it.nombre}</p>
                        {it.codigo && <p className="text-[11px] text-gray-400 font-mono">{it.codigo}</p>}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-gray-700 text-center">{it.cantidad}</td>
                      <td className="px-3 py-2.5 text-sm text-gray-700 text-right whitespace-nowrap">
                        {formatearBs(it.precioUnitario)}
                      </td>
                      <td className="px-3 py-2.5 text-sm font-medium text-[#111827] text-right whitespace-nowrap">
                        {formatearBs(it.subtotal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="flex items-baseline gap-2">
            <span className="text-sm text-gray-500">Total</span>
            <span className="text-xl font-bold text-[#2563EB] whitespace-nowrap">{formatearBs(Number(venta.total))}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 whitespace-nowrap cursor-pointer"
            >
              Cerrar
            </button>
            <button
              onClick={onDescargar}
              disabled={exportando}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#2563EB] text-white text-sm font-medium hover:bg-[#1D4ED8] disabled:opacity-60 whitespace-nowrap cursor-pointer"
            >
              {exportando ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i> Generando...
                </>
              ) : (
                <>
                  <i className="ri-file-pdf-2-line"></i> Descargar PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}